import { detectClientEnvironment } from "./client-environment";

/**
 * Bytes read before an upload starts. This is large enough to force the
 * browser to actually open the file the user picked, and small enough to be
 * free for a readable file.
 */
export const LOCAL_FILE_READ_PROBE_MAX_BYTES = 64 * 1024;

/**
 * Matches the storage transport's no-progress watchdog. A file that cannot
 * deliver its first 64 KB within this window cannot be uploaded either.
 */
export const LOCAL_FILE_READ_PROBE_TIMEOUT_MS = 30_000;

/** Largest picked file that is copied into memory to retry a failed storage POST. */
export const LOCAL_FILE_MATERIALIZE_MAX_BYTES = 64 * 1024 * 1024;

/**
 * Reading a 64 MB file from a slow document provider must still settle well
 * inside the shared 12-minute upload budget.
 */
export const LOCAL_FILE_MATERIALIZE_TIMEOUT_MS = 60_000;

/**
 * A first storage POST that dies this quickly without sending a byte never got
 * to consume the body, so the failure is about the File, not the network.
 */
export const FIRST_STORAGE_TRANSPORT_FAST_FAILURE_WINDOW_MS = 5_000;

/**
 * `aborted` is not a file problem: the caller's abort signal fired while the
 * read was pending. Callers convert it into their cancellation error before
 * any message or telemetry is produced.
 */
export type LocalFileReadFailureKind = "read-error" | "empty-read" | "timeout" | "aborted" | "size-changed";

export type LocalFileReadabilityResult =
  | { isReadable: true; probedBytes: number }
  | { isReadable: false; failureKind: LocalFileReadFailureKind; cause?: unknown };

export type LocalFileMaterializationResult =
  | { isMaterialized: true; blob: Blob }
  | { isMaterialized: false; failureKind: LocalFileReadFailureKind; cause?: unknown };

export type ReadBlobBytes = (blob: Blob) => Promise<ArrayBuffer>;

function readBlobBytesWithBrowserApis(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }
  // Older WebKit and jsdom only expose FileReader.
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      resolve(fileReader.result instanceof ArrayBuffer ? fileReader.result : new ArrayBuffer(0));
    };
    fileReader.onerror = () => reject(fileReader.error ?? new Error("FileReader failed"));
    fileReader.onabort = () => reject(fileReader.error ?? new Error("FileReader aborted"));
    fileReader.readAsArrayBuffer(blob);
  });
}

type BoundedBlobReadOutcome =
  | { outcome: "bytes"; bytes: ArrayBuffer }
  | { outcome: "timeout" }
  | { outcome: "aborted" }
  | { outcome: "error"; cause: unknown };

async function readBlobBytesWithTimeout(
  blob: Blob,
  timeoutMs: number,
  readBlobBytes: ReadBlobBytes,
  signal?: AbortSignal
): Promise<BoundedBlobReadOutcome> {
  if (signal?.aborted) return { outcome: "aborted" };
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let removeAbortListener: (() => void) | null = null;
  const timeoutPromise = new Promise<"timeout">((resolve) => {
    timeoutId = setTimeout(() => resolve("timeout"), Math.max(1, timeoutMs));
  });
  // A read that the browser never settles (a revoked content:// provider can
  // hang) must not outlive the user's cancellation, or the eventual timeout
  // would be reported as an unreadable file after they already moved on.
  const abortPromise = new Promise<"aborted">((resolve) => {
    if (!signal) return;
    const onAbort = () => resolve("aborted");
    signal.addEventListener("abort", onAbort, { once: true });
    removeAbortListener = () => signal.removeEventListener("abort", onAbort);
  });
  try {
    const readResult = await Promise.race([readBlobBytes(blob), timeoutPromise, abortPromise]);
    if (readResult === "timeout") return { outcome: "timeout" };
    if (readResult === "aborted") return { outcome: "aborted" };
    return { outcome: "bytes", bytes: readResult };
  } catch (error) {
    return signal?.aborted ? { outcome: "aborted" } : { outcome: "error", cause: error };
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    removeAbortListener?.();
  }
}

/**
 * Proves that the browser can read the bytes of a user-selected file.
 *
 * An Android WebView can hand the page a `File` whose `content://` source is
 * no longer readable. Every request whose body contains that file then fails
 * immediately as a network error regardless of destination, which is
 * indistinguishable from a connection drop once the upload has started. A
 * bounded read up front separates "unreadable file" from "network down".
 */
export async function probeLocalFileReadability({
  file,
  maximumProbeBytes = LOCAL_FILE_READ_PROBE_MAX_BYTES,
  timeoutMs = LOCAL_FILE_READ_PROBE_TIMEOUT_MS,
  readBlobBytes = readBlobBytesWithBrowserApis,
  signal,
}: {
  file: Blob;
  maximumProbeBytes?: number;
  timeoutMs?: number;
  readBlobBytes?: ReadBlobBytes;
  signal?: AbortSignal;
}): Promise<LocalFileReadabilityResult> {
  if (!(file.size > 0)) {
    // Zero-byte files are rejected by the callers' own validation; there are
    // no bytes to prove readable here.
    return { isReadable: true, probedBytes: 0 };
  }

  let probeBlob: Blob;
  try {
    probeBlob = file.slice(0, Math.min(file.size, Math.max(1, maximumProbeBytes)));
  } catch (error) {
    return { isReadable: false, failureKind: "read-error", cause: error };
  }

  const readOutcome = await readBlobBytesWithTimeout(probeBlob, timeoutMs, readBlobBytes, signal);
  if (readOutcome.outcome === "aborted") return { isReadable: false, failureKind: "aborted" };
  if (readOutcome.outcome === "timeout") return { isReadable: false, failureKind: "timeout" };
  if (readOutcome.outcome === "error") {
    return { isReadable: false, failureKind: "read-error", cause: readOutcome.cause };
  }
  if (!(readOutcome.bytes.byteLength > 0)) return { isReadable: false, failureKind: "empty-read" };
  return { isReadable: true, probedBytes: readOutcome.bytes.byteLength };
}

/**
 * Copies a picked file into memory so a storage POST can carry a body the
 * browser can no longer stream from disk. The readability probe can pass while
 * the send still fails: Chromium rejects a file whose provider reports a
 * different size or modification time than it did at pick time.
 */
export async function materializeLocalFile({
  file,
  timeoutMs = LOCAL_FILE_MATERIALIZE_TIMEOUT_MS,
  readBlobBytes = readBlobBytesWithBrowserApis,
  signal,
}: {
  file: Blob;
  timeoutMs?: number;
  readBlobBytes?: ReadBlobBytes;
  signal?: AbortSignal;
}): Promise<LocalFileMaterializationResult> {
  const readOutcome = await readBlobBytesWithTimeout(file, timeoutMs, readBlobBytes, signal);
  if (readOutcome.outcome === "aborted") return { isMaterialized: false, failureKind: "aborted" };
  if (readOutcome.outcome === "timeout") return { isMaterialized: false, failureKind: "timeout" };
  if (readOutcome.outcome === "error") {
    return { isMaterialized: false, failureKind: "read-error", cause: readOutcome.cause };
  }
  if (file.size > 0 && !(readOutcome.bytes.byteLength > 0)) {
    return { isMaterialized: false, failureKind: "empty-read" };
  }
  // The presigned POST and the completion check were issued for the size the
  // picker reported. A copy with a different length is the file-changed case
  // itself, and uploading it would only fail later at completion; report it
  // as unreadable now instead of leaving an orphaned storage object.
  if (readOutcome.bytes.byteLength !== file.size) {
    return { isMaterialized: false, failureKind: "size-changed" };
  }
  return { isMaterialized: true, blob: new Blob([readOutcome.bytes], { type: file.type }) };
}

/**
 * Only the first direct attempt qualifies. A file provider can fail immediately
 * or hang until the no-progress watchdog fires. Neither proves a network fault;
 * one bounded copy also tests whether the browser can still read the full file.
 * Never copy a partially transmitted or oversized file.
 */
export function shouldMaterializeUploadBodyAfterFirstTransportFailure({
  fileSizeBytes,
  elapsedMs,
  uploadedBytes,
  didTimeOut = false,
  maximumBytes = LOCAL_FILE_MATERIALIZE_MAX_BYTES,
  fastFailureWindowMs = FIRST_STORAGE_TRANSPORT_FAST_FAILURE_WINDOW_MS,
}: {
  fileSizeBytes: number;
  elapsedMs: number;
  uploadedBytes: number;
  didTimeOut?: boolean;
  maximumBytes?: number;
  fastFailureWindowMs?: number;
}): boolean {
  const isBoundedNonEmptyFile = fileSizeBytes > 0 && fileSizeBytes <= maximumBytes;
  const isFastFailureOrTimeout = elapsedMs <= fastFailureWindowMs || didTimeOut;
  const hasSentBytes = uploadedBytes > 0;
  return isBoundedNonEmptyFile && isFastFailureOrTimeout && !hasSentBytes;
}

export function getLocalFileReadCauseName(cause: unknown): string | null {
  if (!cause || typeof cause !== "object") return null;
  const causeName = (cause as { name?: unknown }).name;
  return typeof causeName === "string" && causeName ? causeName : null;
}

export function getLocalFileReadCauseMessage(cause: unknown): string | null {
  if (typeof cause === "string") return cause.slice(0, 300);
  if (!cause || typeof cause !== "object") return null;
  const causeMessage = (cause as { message?: unknown }).message;
  return typeof causeMessage === "string" && causeMessage ? causeMessage.slice(0, 300) : null;
}

/** Low-cardinality size label for Sentry tags; never the exact byte count. */
export function getFileSizeBucket(sizeBytes: number): string {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return "empty";
  const oneMegabyte = 1024 * 1024;
  if (sizeBytes < oneMegabyte) return "under-1mb";
  if (sizeBytes < 5 * oneMegabyte) return "1mb-5mb";
  if (sizeBytes < 10 * oneMegabyte) return "5mb-10mb";
  if (sizeBytes < 25 * oneMegabyte) return "10mb-25mb";
  if (sizeBytes < 100 * oneMegabyte) return "25mb-100mb";
  return "over-100mb";
}

/**
 * Operating system plus the container that owns the file picker, for
 * example `android-native-webview`, `ios-safari`, or `windows-chrome`. The
 * native bridge is definitive; the Android system WebView also marks its user
 * agent with `; wv)` before the bridge handshake completes.
 */
export function getUploadClientPlatformFamily({
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  hasNativeWebViewBridge = typeof window !== "undefined" && Boolean(window.ReactNativeWebView),
}: {
  userAgent?: string;
  hasNativeWebViewBridge?: boolean;
} = {}): string {
  const isAndroidSystemWebView = /;\s*wv\)/.test(userAgent);
  const { operatingSystem, browserFamily } = detectClientEnvironment({
    userAgent,
    isRunningAsMobileApp: hasNativeWebViewBridge,
  });
  const containerFamily = hasNativeWebViewBridge || isAndroidSystemWebView ? "native-webview" : browserFamily;
  return `${operatingSystem}-${containerFamily}`;
}
